import { useEffect, useState } from "react";

export type ResponsiveGridConfig = Record<
  string,
  { minWidth: number; itemsPerRow: number; itemsPerPage: number }
>;

const DEFAULT_CONFIG: ResponsiveGridConfig = {
  "2xl": { minWidth: 1536, itemsPerRow: 4, itemsPerPage: 8 },
  xl: { minWidth: 1280, itemsPerRow: 3, itemsPerPage: 6 },
  lg: { minWidth: 1024, itemsPerRow: 2, itemsPerPage: 4 },
  md: { minWidth: 768, itemsPerRow: 2, itemsPerPage: 4 },
  sm: { minWidth: 640, itemsPerRow: 2, itemsPerPage: 4 },
  xs: { minWidth: 0, itemsPerRow: 1, itemsPerPage: 4 },
} as const;

export const useResponsiveGridLayout = (
  config: ResponsiveGridConfig = DEFAULT_CONFIG,
  debounceDelay = 10,
) => {
  const [currentConfig, setCurrentConfig] =
    useState<keyof ResponsiveGridConfig>("xs");

  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>;

    const updateConfig = () => {
      const width = window.innerWidth;
      const sortedBreakpoints = Object.entries(config).sort(
        (a, b) => b[1].minWidth - a[1].minWidth,
      );

      for (const [key, { minWidth }] of sortedBreakpoints) {
        if (width >= minWidth) {
          setCurrentConfig(key as keyof ResponsiveGridConfig);
          break;
        }
      }
    };

    const handleResize = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(updateConfig, debounceDelay);
    };

    window.addEventListener("resize", handleResize);
    updateConfig();

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(debounceTimer);
    };
  }, [config, debounceDelay]);

  return config[currentConfig];
};
